import type { RenderInput } from "./types.js";
import { resolveSiteTheme } from "../theme/index.js";

interface FontEntry {
  family: string;
}

function collectGoogleFonts(input: RenderInput): FontEntry[] {
  const seen = new Map<string, FontEntry>();

  const pageFonts = input.settings.googleFonts ?? [];
  for (const family of pageFonts) {
    const key = family.toLowerCase();
    if (!seen.has(key)) {
      seen.set(key, { family });
    }
  }

  const themeFonts = resolveSiteTheme(input.siteContext?.globalTheme).fonts;
  if (themeFonts?.heading?.googleFont) {
    const key = themeFonts.heading.googleFont.toLowerCase();
    if (!seen.has(key)) {
      seen.set(key, { family: themeFonts.heading.googleFont });
    }
  }
  if (themeFonts?.body?.googleFont) {
    const key = themeFonts.body.googleFont.toLowerCase();
    if (!seen.has(key)) {
      seen.set(key, { family: themeFonts.body.googleFont });
    }
  }

  return [...seen.values()];
}

function buildGoogleFontStylesheetHref(fonts: readonly FontEntry[]): string {
  const families = fonts
    .map((font) => `family=${encodeURIComponent(font.family)}:wght@400;600;700`)
    .join("&");

  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

function buildGoogleFontPreloadHref(font: FontEntry): string {
  return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(font.family)}:wght@400;600;700&display=swap`;
}

export function buildFontLinks(input: RenderInput): string {
  const fonts = collectGoogleFonts(input);
  if (fonts.length === 0) return "";

  const stylesheetHref = buildGoogleFontStylesheetHref(fonts);
  const parts: string[] = [
    '<link rel="dns-prefetch" href="//fonts.googleapis.com">',
    '<link rel="dns-prefetch" href="//fonts.gstatic.com">',
    '<link rel="preconnect" href="https://fonts.googleapis.com">',
    '<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>',
  ];

  for (const font of fonts) {
    parts.push(
      `<link rel="preload" as="style" href="${buildGoogleFontPreloadHref(font)}">`,
    );
  }

  parts.push(`<link rel="stylesheet" href="${stylesheetHref}">`);
  return parts.join("\n");
}
